import { getStoredUtmParams, getStoredRefCode } from './utmService';

/**
 * Shape of the JSON body posted to `POST /api/auth/complete` by every caller.
 *
 * The backend reads each field via `request_data.get(...)` (see
 * `backend/routes/auth.py::complete_authentication`) and passes UTM /
 * acquisition fields through to `provision_new_account`, where they become
 * `User.skeleton.acquisition.*`. Fields left `undefined` are omitted by
 * `JSON.stringify` and treated as `None` server-side.
 */
export interface AuthCompletePayload {
  skip_space_creation: boolean;
  funnel_id?: string;
  persona?: string;
  page_group?: string;
  page_slug?: string;
  onboarding_data?: { role?: string; interest?: string };
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  first_landing_page?: string;
  referrer?: string;
  ref_code?: string;
}

/**
 * Overrides accepted by the builder. Anything left undefined falls back to
 * the appropriate localStorage / utmService source. Callers that already
 * have values in closure (e.g. `useAuthForm` knows its `funnelId` prop)
 * should pass them so the payload reflects the live React state even if
 * localStorage is stale.
 */
export interface AuthCompletePayloadOverrides {
  skip_space_creation: boolean;
  funnel_id?: string;
  persona?: string;
  page_group?: string;
  page_slug?: string;
  onboarding_data?: { role?: string; interest?: string };
}

function readPendingOnboardingData(): { role?: string; interest?: string } | undefined {
  try {
    const stored = localStorage.getItem('pre_signup_onboarding_answers');
    if (!stored) return undefined;
    const parsed = JSON.parse(stored);
    const role = parsed.role || undefined;
    const interest = parsed.interest || parsed.interests || undefined;
    if (!role && !interest) return undefined;
    return { role, interest };
  } catch {
    return undefined;
  }
}

/**
 * Build the JSON body for `POST /api/auth/complete`.
 *
 * Why this exists: four call sites in the app post to `/auth/complete` and
 * race each other to provision the same Auth0 user. Whichever wins the
 * unique-index insert in `provision_new_account` is the one whose UTM,
 * funnel, persona, and affiliate ref_code get persisted onto the new
 * `User.skeleton.acquisition`. Any caller that posts a partial body can
 * orphan a new signup with no attribution at all. Centralizing the body
 * here guarantees every caller sends the same complete shape.
 *
 * Order of precedence per field:
 *   1. Explicit override passed in (caller has live closure value)
 *   2. localStorage fallback (set earlier in the flow, e.g. by
 *      `useAuthForm.handleGoogleAuth` before opening the OAuth popup)
 *   3. `undefined` (omitted from the serialized body)
 */
export function buildAuthCompletePayload(
  overrides: AuthCompletePayloadOverrides,
): AuthCompletePayload {
  const utm = getStoredUtmParams();

  return {
    skip_space_creation: overrides.skip_space_creation,
    funnel_id:
      overrides.funnel_id ?? (localStorage.getItem('pendingFunnelId') || undefined),
    persona:
      overrides.persona ?? (localStorage.getItem('pendingPersona') || undefined),
    page_group:
      overrides.page_group ?? (localStorage.getItem('pendingPageGroup') || undefined),
    page_slug:
      overrides.page_slug ?? (localStorage.getItem('pendingPageSlug') || undefined),
    onboarding_data: overrides.onboarding_data ?? readPendingOnboardingData(),
    utm_source: utm?.utm_source,
    utm_medium: utm?.utm_medium,
    utm_campaign: utm?.utm_campaign,
    utm_content: utm?.utm_content,
    utm_term: utm?.utm_term,
    first_landing_page: utm?.first_landing_page,
    referrer: utm?.referrer,
    ref_code: getStoredRefCode() || undefined,
  };
}
